import { Button, Icon } from '@vwfs-bronson/bronson-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type MenuPosition = {
  left: number;
  top: number;
};

export type RowActionItem = {
  danger?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  icon: string;
  label: string;
  onSelect: () => void;
};

export function RowActionsMenu({
  deleteDisabled,
  deleteDisabledReason,
  disabled,
  disabledReason,
  items,
  label,
  onDelete,
  onEdit,
}: {
  deleteDisabled?: boolean;
  deleteDisabledReason?: string;
  disabled?: boolean;
  disabledReason?: string;
  items?: RowActionItem[];
  label: string;
  onDelete: () => void;
  onEdit: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition>({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const focusTrigger = useCallback(() => {
    triggerRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;

    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const menuWidth = 180;
    const menuHeight = (items?.length ?? 2) * 42 + 12;
    const gap = 6;
    const viewportPadding = 8;
    const left = Math.min(Math.max(rect.right - menuWidth, viewportPadding), window.innerWidth - menuWidth - viewportPadding);
    const bottomTop = rect.bottom + gap;
    const top =
      bottomTop + menuHeight > window.innerHeight - viewportPadding
        ? Math.max(rect.top - menuHeight - gap, viewportPadding)
        : bottomTop;

    setPosition({ left, top });
  }, [items?.length]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateMenuPosition();
    const firstMenuItem = menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
    firstMenuItem?.focus();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      setIsOpen(false);
      focusTrigger();
    };

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [focusTrigger, isOpen, updateMenuPosition]);

  const closeAndRun = (action: () => void) => {
    setIsOpen(false);
    action();
  };

  const menuItems: RowActionItem[] = items ?? [
    { icon: 'semantic-edit', label: 'Edit', onSelect: onEdit },
    {
      danger: true,
      disabled: deleteDisabled,
      disabledReason: deleteDisabledReason,
      icon: 'semantic-delete',
      label: 'Delete',
      onSelect: onDelete,
    },
  ];

  return (
    <div className="app-row-actions" ref={triggerRef}>
      <Button
        ariaExpanded={isOpen ? 'true' : 'false'}
        aria-haspopup="menu"
        ariaLabel={disabled ? disabledReason || `Actions unavailable for ${label}` : `Actions for ${label}`}
        className="app-table-icon-button"
        disabled={disabled}
        icon="semantic-more"
        onClick={() => setIsOpen(open => !open)}
        secondary
        title={disabled ? disabledReason : `Actions for ${label}`}
      />
      {isOpen &&
        createPortal(
          <div
            className="app-row-actions-menu"
            ref={menuRef}
            role="menu"
            style={{ left: position.left, top: position.top }}
          >
            {menuItems.map(item => (
              <button
                aria-label={`${item.label} ${label}`}
                className={item.danger ? 'app-row-actions-menu__item app-row-actions-menu__item--danger' : 'app-row-actions-menu__item'}
                disabled={item.disabled}
                key={item.label}
                onClick={() => closeAndRun(item.onSelect)}
                role="menuitem"
                title={item.disabled ? item.disabledReason : undefined}
                type="button"
              >
                <Icon name={item.icon} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
